import { format } from 'date-fns';

/** Default planning time (9:00 AM) used when a post has no time yet. */
export const DEFAULT_PLANNING_TIME = '09:00';

/**
 * 48 half-hour options for a schedule time `<Select>` — shared by the post
 * inspector's `DateTimePlanningControl` and the curation-flow
 * `ScheduleDraftDialog` so both surfaces offer identical time slots.
 */
export const TIME_SELECT_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hours = Math.floor(index / 2);
  const minutes = index % 2 === 0 ? 0 : 30;
  const value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  const labelDate = new Date();
  labelDate.setHours(hours, minutes, 0, 0);
  return { value, label: format(labelDate, 'h:mm a') };
});

/**
 * `Date` → `"HH:mm"` select value, snapped to the nearest of the 48 half-hour
 * options above. A raw off-grid time (e.g. a post scheduled at 1:56 PM,
 * however it got there) matched none of `TIME_SELECT_OPTIONS`'s exact
 * values, so `<Select>` rendered with nothing selected — an empty-looking
 * "Draft time" row despite the post genuinely having a time. Rounding
 * trades a few minutes of display precision (the real value is unaffected
 * until the user actually touches this control) for the select always
 * showing *some* selected option.
 */
export function timeValueFromDate(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return DEFAULT_PLANNING_TIME;
  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  const roundedMinutes = Math.round(totalMinutes / 30) * 30;
  const hours = Math.floor(roundedMinutes / 60) % 24;
  const minutes = roundedMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * `Date` → exact `"HH:mm"`, unrounded — the value format a native
 * `<input type="time">` expects. Unlike {@link timeValueFromDate} this never
 * snaps to a half-hour grid, so a post planned for 1:56 PM reads back as
 * 1:56 PM instead of the 2:00 PM the `<Select>` had to invent.
 */
export function exactTimeValueFromDate(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return DEFAULT_PLANNING_TIME;
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** Apply an `"HH:mm"` select value onto a calendar `Date` (seconds zeroed). */
export function applyTimeToDate(date: Date, timeValue: string): Date {
  const [hours = '9', minutes = '0'] = timeValue.split(':');
  const next = new Date(date);
  next.setHours(Number(hours), Number(minutes), 0, 0);
  return next;
}

export function viewerTimeZoneName(at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZoneName: 'long' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? '';
  } catch {
    return '';
  }
}

export function formatWithZone(at: Date): string {
  try {
    const zone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName')?.value;
    const base = format(at, 'MMM d, h:mm a');
    return zone ? `${base} ${zone}` : base;
  } catch {
    return format(at, 'MMM d, h:mm a');
  }
}
